import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Accordion, Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "../../hook/templates";
import { TemplateKeyAndCategory } from "../../models/templates";
import { useAppSelector } from "../../store";
import { queryClient } from "../../utils/http/queryClient";
import { fetchTranslationUsage, promoteTemplate, renderTemplate } from "../../utils/http/templates";
import { transformPathToUrl } from "../../utils/url";

function ModalPromote({
  showSendTestModal,
  setShowSendTestModal,
}: {
  showSendTestModal: any;
  setShowSendTestModal: any;
}) {
  const navigate = useNavigate();

  const selectedPath = useAppSelector((state) => state.templates.selectedPath);
  const selectedVersion = useAppSelector((state) => state.templates.selectedVersion);

  const [checkedNames, setCheckedNames] = useState<Record<string, boolean>>();
  const [draftTranslationKeys, setDraftTranslationKeys] = useState<string[]>([]);
  const [overallImpactedTemplates, setOverallImpactedTemplates] = useState<TemplateKeyAndCategory[]>([]);

  const { data: templateRenderReturn } = useQuery({
    queryKey: ["templateRender", selectedPath!.namespace, selectedPath!.name, selectedVersion],
    queryFn: ({ signal }) => renderTemplate({ signal, key: selectedPath!, version: selectedVersion }),
    staleTime: 2000,
  });

  const translationsUsed = templateRenderReturn?.template?.metadata?.translations_used || [];

  // Fetch translations for the keys we want to translate
  const dbTranslations = useTranslations(translationsUsed, selectedVersion, !!templateRenderReturn);

  // Fetch the templates in which the translations are used
  const { data: translationsWithAutomations } = useQuery<{
    [key: string]: TemplateKeyAndCategory[];
  }>({
    queryKey: ["translationUsage", draftTranslationKeys, selectedVersion],
    queryFn: async ({ signal }) => {
      const translationsWithAutomations = await fetchTranslationUsage({
        signal,
        keys: draftTranslationKeys,
      });

      // Collect all templates from all keys and remove duplicates
      const allTemplates = Object.values(translationsWithAutomations).flat();
      const uniqueTemplates = allTemplates.filter(
        (item, index, self) => index === self.findIndex((t) => t.namespace === item.namespace && t.name === item.name)
      );
      setOverallImpactedTemplates(uniqueTemplates);

      return translationsWithAutomations;
    },
    enabled: draftTranslationKeys.length > 0,
  });

  useEffect(() => {
    if (dbTranslations.data) {
      // Filter keys where the first element has version === "draft"
      const draftKeys = Object.keys(dbTranslations.data).filter((key) => {
        const translations = dbTranslations.data![key];
        return translations.length > 0 && translations[0].version === "draft";
      });

      setDraftTranslationKeys(draftKeys);
    }
  }, [dbTranslations.data]);

  // Group translations by name (second part), collecting namespaces
  const groupedByName = draftTranslationKeys.reduce((acc, translation) => {
    const parts = translation.split(".");
    const namespace = parts[0];
    const name = parts[1];
    const remainingParts = parts.slice(2).join(".");
    const nameKey = `${namespace}.${name}`;

    if (!acc[nameKey]) {
      acc[nameKey] = {
        namespace,
        name,
        items: [],
      };
    }
    if (remainingParts) {
      acc[nameKey].items.push(remainingParts);
    }
    return acc;
  }, {} as Record<string, { namespace: string; name: string; items: string[] }>);

  const handleCheckboxChange = (nameKey: string, checked: boolean) => {
    setCheckedNames((prev) => ({
      ...prev,
      [nameKey]: checked,
    }));
  };

  // Sort entries alphabetically by nameKey
  const sortedEntries = Object.entries(groupedByName).sort(([a], [b]) => a.localeCompare(b));

  // Initialize all checkboxes as checked if not already set
  const allCheckedNames = sortedEntries.reduce((acc, [nameKey]) => {
    if (checkedNames?.[nameKey] === undefined) {
      acc[nameKey] = true;
    } else {
      acc[nameKey] = checkedNames[nameKey];
    }
    return acc;
  }, {} as Record<string, boolean>);

  const { mutateAsync: mutatePromoteDraft } = useMutation({
    mutationFn: promoteTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["templatesSummary"] });
      navigate(transformPathToUrl({ path: selectedPath!, version: "live" }));
    },
  });

  const handlePromote = async () => {
    const selectedKeys = sortedEntries
      .filter(([nameKey]) => allCheckedNames[nameKey])
      .flatMap(([nameKey, data]) => data.items.map((item) => `${nameKey}.${item}`));

    try {
      await mutatePromoteDraft({
        key: selectedPath!,
        translationKeys: selectedKeys,
      });
      setShowSendTestModal(false);
    } catch (error) {
      // Error handling - modal stays open if promotion fails
      console.error("Failed to promote template:", error);
    }
  };

  if (!templateRenderReturn) return <></>;

  return (
    <Modal size="xl" show={showSendTestModal} onHide={() => setShowSendTestModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Template Promotion</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row>
            <Col>
              You've overwritten several translations. This will affect {overallImpactedTemplates.length} templates,
              including the one you want to promote. If you're sure this is fine, you can go ahead. If you want to
              exclude some translations, you can do so below:
              <br />
              <br />
              <Accordion>
                {sortedEntries.map(([nameKey, data], index) => (
                  <Accordion.Item key={index} eventKey={String(index)}>
                    <Accordion.Header>
                      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Form.Check
                          type="checkbox"
                          checked={allCheckedNames[nameKey]}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(nameKey, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="me-2"
                        />
                        <span style={{ flex: "0 0 200px", fontWeight: "500" }}>{data.namespace}</span>
                        <span style={{ flex: "1" }}>{data.name}</span>
                        <span style={{ flex: "0 0 120px", textAlign: "right", color: "#666" }}>
                          {(() => {
                            const translationCount = data.items.length;
                            return `${translationCount} translation${translationCount !== 1 ? "s" : ""}`;
                          })()}
                        </span>
                        <span style={{ flex: "0 0 120px", textAlign: "right", color: "#666", paddingRight: "20px" }}>
                          {(() => {
                            // Calculate unique templates for this nameKey
                            const templatesSet = new Set<string>();
                            data.items.forEach((item) => {
                              const fullKey = `${nameKey}.${item}`;
                              const templates = translationsWithAutomations?.[fullKey] || [];
                              templates.forEach((t) => {
                                templatesSet.add(`${t.namespace}.${t.name}`);
                              });
                            });
                            return `${templatesSet.size} template${templatesSet.size !== 1 ? "s" : ""}`;
                          })()}
                        </span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {(() => {
                        // Group items by their template usage
                        const groupedByTemplates = data.items.reduce((acc, item) => {
                          const fullKey = `${nameKey}.${item}`;
                          const templates = translationsWithAutomations?.[fullKey] || [];

                          // Create a unique key for this set of templates
                          const templateKey = templates
                            .map((t) => `${t.namespace}.${t.name}`)
                            .sort()
                            .join("|");

                          if (!acc[templateKey]) {
                            acc[templateKey] = {
                              items: [],
                              templates: templates,
                            };
                          }
                          acc[templateKey].items.push(item);
                          return acc;
                        }, {} as Record<string, { items: string[]; templates: TemplateKeyAndCategory[] }>);

                        return Object.values(groupedByTemplates).map((group, groupIndex) => (
                          <Row key={groupIndex} style={{ marginBottom: "1rem" }}>
                            <Col md={6}>
                              <h6>Translation{group.items.length > 1 ? "s" : ""}:</h6>
                              <ul>
                                {group.items.map((item, itemIndex) => (
                                  <li key={itemIndex}>{item}</li>
                                ))}
                              </ul>
                            </Col>
                            <Col md={6}>
                              <h6>Used in Templates:</h6>
                              <ul>
                                {group.templates.length > 0 ? (
                                  group.templates
                                    .sort((a, b) => {
                                      const aKey = `${a.namespace}.${a.name}`;
                                      const bKey = `${b.namespace}.${b.name}`;
                                      return aKey.localeCompare(bKey);
                                    })
                                    .map((template, templateIndex) => {
                                      const isCurrentTemplate =
                                        template.namespace === selectedPath?.namespace &&
                                        template.name === selectedPath?.name;

                                      return (
                                        <li key={templateIndex}>
                                          {isCurrentTemplate ? (
                                            "current template"
                                          ) : (
                                            <a
                                              href={`/templates/${template.namespace}/${template.category}/${template.name}/live`}
                                              target="_blank"
                                            >
                                              {template.namespace}.{template.category}.{template.name}
                                            </a>
                                          )}
                                        </li>
                                      );
                                    })
                                ) : (
                                  <li>No templates found</li>
                                )}
                              </ul>
                            </Col>
                          </Row>
                        ));
                      })()}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowSendTestModal(false)}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePromote}>
          Promote
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalPromote;
